import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { combineLatestWith, of, Subject, switchMap } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzDrawerModule, NzDrawerService } from 'ng-zorro-antd/drawer';
import { AppointmentFacade } from '../../appointment-facade.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzFormModule } from 'ng-zorro-antd/form';

import { TranslateModule } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NgxPermissionsModule } from 'ngx-permissions';
import { AsyncPipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { NzContentComponent, NzFooterComponent, NzLayoutComponent } from 'ng-zorro-antd/layout';
import {
  DatepickerComponent,
  InputComponent,
  SelectComponent,
  SelectSearchComponent,
  TextareaComponent,
  TimePickerComponent
} from '@beautyapp/form';
import { AppointmentServicesComponent } from '../appointment-services/appointment-services.component';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { BookingAuditHistoryComponent } from '../booking-audit-history/booking-audit-history.component';
import { seedBulkExpence } from './seed-bulk-expence';
import { BirthdayBadgeComponent } from '@beautyapp/customer-360';

@UntilDestroy()
@Component({
  selector: 'beautyapp-add-appointment',
  templateUrl: './add-appointment.component.html',
  styleUrls: ['./add-appointment.component.scss'],
  imports: [
    NzGridModule,
    ReactiveFormsModule,
    NzFormModule,
    TranslateModule,
    NzButtonModule,
    NzIconModule,
    NzInputModule,
    FormsModule,
    NzTagModule,
    NzCheckboxModule,
    NzTypographyModule,
    NzSpaceModule,
    NzModalModule,
    NzDrawerModule,
    NzPopconfirmModule,
    NzDividerModule,
    NzCardModule,
    NzSpinComponent,
    NgxPermissionsModule,
    AsyncPipe,
    NzLayoutComponent,
    NzContentComponent,
    NzFooterComponent,
    DecimalPipe,
    SelectSearchComponent,
    NzTooltipDirective,
    SelectComponent,
    TimePickerComponent,
    DatepickerComponent,
    TextareaComponent,
    AppointmentServicesComponent,
    InputComponent,
    NgTemplateOutlet,
    NzTabsModule,
    BookingAuditHistoryComponent,
    BirthdayBadgeComponent,
  ]
})
export class AddAppointmentComponent implements OnInit, OnDestroy {
  readonly data = inject(NZ_MODAL_DATA);
  private modal: NzModalRef = inject(NzModalRef);
  public drawerService: NzDrawerService = inject(NzDrawerService);
  public apartmentFacade: AppointmentFacade = inject(AppointmentFacade);

  get employeeId() {
    return this.data.employeeId;
  }

  get bookingId() {
    return this.data.bookingId;
  }

  get date() {
    return this.data.date;
  }

  get endDate() {
    return this.data.endDate;
  }

  get userId() {
    return this.data.userId;
  }

  get services() {
    return this.data.services;
  }

  get description() {
    return this.data.description;
  }

  get onlineBookingId() {
    return this.data.onlineBookingId;
  }

  form: UntypedFormGroup = this.apartmentFacade.form;

  sub$: Subject<any> = new Subject<any>();

  constructor() {
    this.apartmentFacade.modalRef = this.modal;
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
    this.apartmentFacade.destroy();
  }

  ngOnInit(): void {
    // this.apartmentFacade.init();
    // beaut-366: preload a first page of customers so the select shows records
    // as soon as the field is focused (empty search → BE returns the first 50),
    // instead of the empty "no customer" state.
    this.apartmentFacade.searchCustomer('');
    if (this.data.date) {
      this.apartmentFacade.dates = this.data.date;
    }
    if (this.data.bookingId) {
      this.apartmentFacade.bookingId = this.data.bookingId;
      this.apartmentFacade.getById().pipe(takeUntil(this.sub$)).subscribe();
    }
    if (this.data.employeeId) {
      this.apartmentFacade.employeeId = this.data.employeeId;
      this.apartmentFacade
        .getSelectedEmployee()
        .pipe(takeUntil(this.sub$))
        .subscribe();
    }

    // if (this.data.userId) {
    //   this.apartmentFacade.userId = this.data.userId;
    //   this.apartmentFacade
    //     .getCustomerById()
    //     .pipe(takeUntil(this.sub$))
    //     .subscribe();
    // }

    if (this.data.endDate) {
      this.apartmentFacade.form.get('endTime')?.setValue(this.data.endDate);
    }

    if (this.data.services) {
      const serviceIds = this.data.services.map((m: any) => m.id);
      this.apartmentFacade.selectedEmployee$
        .pipe(
          combineLatestWith(
            this.apartmentFacade.getServiceByIds(serviceIds)
          ),
          takeUntil(this.sub$)
        )
        .subscribe(([employee, services]) => {
          if (employee) {
            const servicesMap = services.map((m: any) => ({
              serviceId: m.id,
              service: m,
              price: m.price,
              quantity: 1,
              salePrice: m.salePrice,
              employeeId: employee?.id,
              employeeName: employee?.user?.fullName,
              salaryPercent: employee?.outputPercent || 0,
              salaryFixed: 0,
              salaryPercentFromPrice: false,
              // autoFix off → 0; autoFix+fixSum → fixSum; autoFix alone → products
              // (bulk-selected services skipped the seeding the other flows do).
              expence: seedBulkExpence(m),
              description: null,
              isAdditionalService: false,
              time: m.time
            }));
            this.apartmentFacade.services.next(servicesMap);
          }
        });
    }

    if (this.description) {
      this.apartmentFacade.form
        .get('description')
        ?.setValue(this.data.description);
    }

    if (this.onlineBookingId) {
      this.apartmentFacade.onlineBookingId = this.data.onlineBookingId;
    }

    this.form
      .get('paymentType')
      ?.valueChanges.pipe(
      untilDestroyed(this),
      switchMap((res) => this.apartmentFacade.getServiceSum$()),
      tap((res) => {
        this.apartmentFacade.paymentUpdate(res);
      })
    )
      .subscribe();

    this.form
      .get('employeeId')
      ?.valueChanges.pipe(
      untilDestroyed(this),
      filter((res) => !!res),
      switchMap((res: any) => {
        if (res) {
          this.apartmentFacade.prevEmployee = this.apartmentFacade.getCurrentEmployee;
          return this.apartmentFacade.getSelectedEmployee();
        }
        return of(null);
      })
    )
      .subscribe();
  }

  submit() {
    this.apartmentFacade.isLoading.next(true);
    this.apartmentFacade.submit().pipe(takeUntil(this.sub$)).subscribe();
  }

  complete() {
    this.apartmentFacade.complete().pipe(takeUntil(this.sub$)).subscribe();
  }
}
